import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { parseJwt, fetchOidcUserInfo, fetchIdentityProfile, getLocalUserInfo, setLocalUserInfo } from '@/services/authApi';

const OAuthCallback: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleOAuthCallback } = useAuth();

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!provider) {
        setError('Missing authentication provider.');
        return;
      }

      if (!code) {
        setError('Missing authorization code. The authentication request may have been denied.');
        return;
      }

      try {
        await handleOAuthCallback(provider, code, state ?? '');

        // Route new users (no profile yet) to onboarding; returning users to coach
        const token = localStorage.getItem('five_ai_access_token');
        if (token) {
          try {
            // Access tokens don't carry identity claims for OAuth users —
            // call the OIDC userinfo endpoint which reads from MongoDB.
            const enriched: Record<string, string> = {};
            try {
              const oidc = await fetchOidcUserInfo(token);
              if (oidc.email)       enriched.signUpEmail = oidc.email;
              if (oidc.given_name)  enriched.firstName   = oidc.given_name;
              if (oidc.family_name) enriched.lastName    = oidc.family_name;
              // Fallback: derive from "name" if first/last weren't separate
              if (!enriched.firstName && oidc.name) {
                const parts = oidc.name.split(' ');
                enriched.firstName = parts[0] ?? '';
                enriched.lastName  = parts.slice(1).join(' ') || enriched.lastName || '';
              }
              if (oidc.locale)   enriched.locale   = oidc.locale;
              if (oidc.zoneinfo) enriched.timezone = oidc.zoneinfo;
            } catch {
              // OIDC call failed — fall back to whatever the JWT contains
              const decoded = parseJwt(token);
              if (decoded.email)     enriched.signUpEmail = decoded.email;
              if (decoded.firstName) enriched.firstName   = decoded.firstName;
              if (decoded.lastName)  enriched.lastName    = decoded.lastName;
            }

            // Fallback chain: OIDC → JWT claims → identity profile → JWT sub
            let identityProfile: Awaited<ReturnType<typeof fetchIdentityProfile>> | null = null;
            if (!enriched.signUpEmail) {
              try {
                identityProfile = await fetchIdentityProfile(token);
                if (identityProfile.email)     enriched.signUpEmail = identityProfile.email;
                if (identityProfile.firstName) enriched.firstName = enriched.firstName || identityProfile.firstName;
                if (identityProfile.lastName)  enriched.lastName  = enriched.lastName  || identityProfile.lastName;
              } catch {
                // Identity profile fetch failed — try JWT sub as email
                const decoded = parseJwt(token);
                if (decoded.id && decoded.id.includes('@')) enriched.signUpEmail = decoded.id;
              }
            } else {
              try {
                identityProfile = await fetchIdentityProfile(token);
              } catch {
                // optional: we already have email/name
              }
            }

            const email = enriched.signUpEmail || parseJwt(token).email || '';
            // Check if profile exists — use direct fetch with token to avoid interceptor timing issues
            let hasProfile = false;
            if (email) {
              try {
                const profileRes = await fetch(
                  `/api/user-profile/api/v1/profiles/email/${encodeURIComponent(email)}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                hasProfile = profileRes.ok;
              } catch {
                hasProfile = false;
              }
            }

            if (!hasProfile) {
              // NEW USER — no profile yet → go to onboarding
              try {
                const existing = getLocalUserInfo();
                const regFirstName = searchParams.get('reg_firstName');
                const regLastName = searchParams.get('reg_lastName');
                const regPhoneCC = searchParams.get('reg_phoneCountryCode');
                const regPhone = searchParams.get('reg_phoneNumber');
                const regGender = searchParams.get('reg_gender');
                const regDob = searchParams.get('reg_dateOfBirth');

                setLocalUserInfo({
                  signUpEmail: enriched.signUpEmail || existing.signUpEmail || '',
                  email: enriched.signUpEmail || existing.email || '',
                  firstName: regFirstName || enriched.firstName || existing.firstName || '',
                  lastName: regLastName || enriched.lastName || existing.lastName || '',
                  phoneCountryCode: regPhoneCC || identityProfile?.phoneNumber?.countryCode || existing.phoneCountryCode,
                  phoneNumber: regPhone || identityProfile?.phoneNumber?.number || existing.phoneNumber,
                  dateOfBirth: regDob || identityProfile?.dateOfBirth || existing.dateOfBirth,
                  gender: regGender || identityProfile?.gender || existing.gender,
                });

                // Only set "returned from registration" flags if AuthPage already set them
                // (fromOnboardingFlow=true). This prevents standalone /login → OAuth from
                // skipping the survey when the user lands on /onboarding as a new user.
                // AuthPage sets these BEFORE OAuth redirect when fromOnboardingFlow=true.
                // We only reinforce them here (e.g., if cleared by cleanup), not create new ones.
                const alreadySetByAuthPage = !!localStorage.getItem('five_ai_onboarding_auth_provider');
                if (alreadySetByAuthPage) {
                  try {
                    localStorage.setItem('five_ai_onboarding_returned_from_registration', '1');
                  } catch {
                    /* ignore */
                  }
                }
              } catch {
                // ignore
              }
              navigate('/onboarding', { replace: true });
              return;
            }

            // EXISTING USER — has profile → clear stale onboarding flags and go to coach
            try {
              localStorage.removeItem('five_ai_onboarding_session_id');
              localStorage.removeItem('five_ai_onboarding_returned_from_registration');
              localStorage.removeItem('five_ai_onboarding_auth_provider');
              localStorage.removeItem('five_ai_onboarding_intent_email');
            } catch {
              /* ignore */
            }
          } catch {
            // Profile check failed — fall through to default route
          }
        }

        // Use preserved ?next= from login page (e.g. /coach for existing users from home/Get started).
        let dest = '/coach';
        try {
          const stored = localStorage.getItem('five_ai_login_next_dest');
          if (stored && typeof stored === 'string' && stored.startsWith('/') && !stored.startsWith('//')) {
            dest = stored;
          }
          localStorage.removeItem('five_ai_login_next_dest');
        } catch {
          /* ignore */
        }
        navigate(dest, { replace: true });
      } catch (err) {
        // OAuth error — clean up stale flags so retry starts fresh
        try {
          localStorage.removeItem('five_ai_onboarding_returned_from_registration');
          localStorage.removeItem('five_ai_onboarding_auth_provider');
        } catch {
          /* ignore */
        }
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred during authentication.';
        setError(message);
      }
    };

    processCallback();
  }, [provider, searchParams, handleOAuthCallback, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <svg
              className="h-6 w-6 text-destructive"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-lg font-semibold text-foreground">Authentication Failed</h2>
          <p className="mb-6 text-sm text-muted-foreground">{error}</p>
          <Link
            to="/auth"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <BrandedLoading message="Completing authentication…" iconSize="md" />
    </div>
  );
};

export default OAuthCallback;
